import {
  createContext,
  ReactNode,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { Unit } from "../@data/unit";
import { collection, getDocs, query } from "firebase/firestore";
import { firestore } from "../services/firebase";
import { useHistory } from "react-router-dom";
import { parseDaysWithRushHoursFromSaveDatabase } from "../utils";

export interface UnitsContextModel {
  units: Unit[];
  loadingUnits: boolean;
  handleOpenUnitDetails: (unit: Unit) => void;
}

export const UnitsContext = createContext<UnitsContextModel>(
  {} as UnitsContextModel
);

export const UnitsProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const history = useHistory();
  const [units, setUnits] = useState<Unit[]>([] as Unit[]);
  const [loadingUnits, setLoadingUnits] = useState<boolean>(true);

  const handleGetUnits = useCallback(async () => {
    const q = query(collection(firestore, "units"));
    getDocs(q)
      .then((snapshot) => {
        let unitsData = [] as Unit[];
        snapshot.forEach((unit) =>
          unitsData.push({
            ...(unit.data() as Unit),
            unitId: unit.id,
            daysWithRushHoursList: unit.data().daysWithRushHoursList
              ? parseDaysWithRushHoursFromSaveDatabase(
                  unit.data().daysWithRushHoursList
                )
              : {},
          })
        );
        setUnits(unitsData);
      })
      .finally(() => setLoadingUnits(false));
  }, []);

  useEffect(() => {
    handleGetUnits();
  }, [handleGetUnits]);

  const handleOpenUnitDetails = useCallback(
    (unitClicked: Unit) => {
      history.push(`/units/${unitClicked.unitId}`);
    },
    [history]
  );

  return (
    <UnitsContext.Provider
      value={{
        units,
        loadingUnits,
        handleOpenUnitDetails,
      }}
    >
      {children}
    </UnitsContext.Provider>
  );
};

export const useUnits = () => useContext(UnitsContext);
